/**
 * @fileoverview Step 4: Talk with the Assessor Page
 * @module ArgumentsPage
 * 
 * Business Context:
 * - Prepares users for their meeting with the property assessor
 * - Features contextual videos explaining meeting format and argument structure
 * - Provides AI-generated arguments based on Steps 2 & 3 evidence
 * - Includes workup packet combining all documented evidence
 * 
 * Layout:
 * - LEFT: Course navigation (video list + activity buttons)
 * - RIGHT: Video player OR Build Your Case wizard activity
 * 
 * Content Structure:
 * 1. Introduction: Talk with the Assessor
 * 2. What to Expect in the Meeting (6 steps)
 * 3. How to Structure Your Argument
 * 4. How to Walk Through Your Evidence (4 steps)
 * 5. Counter Offers, Next Steps, and Practical Tips
 * → Activity: Build Your Case (4-phase wizard)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import VideoPlayer from '../components/course/VideoPlayer';
import { BuildYourCaseWizard } from '../components/argument';
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  MessageCircle,
  Play,
  CheckCircle2,
  Clock,
  ChevronRight,
  ChevronDown,
  BookOpen,
  Target,
  Sparkles
} from 'lucide-react';

interface VideoContent {
  id: string;
  title: string;
  description: string;
  duration: string;
  durationSeconds: number;
  videoUrl: string;
  sectionGroup: string;
}

// Module ID for Step 4
const STEP4_MODULE_ID = 'module-04';

const ArgumentsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Video state
  const [videoContent, setVideoContent] = useState<VideoContent[]>([]);
  const [activeVideo, setActiveVideo] = useState<VideoContent | null>(null);
  const [completedVideos, setCompletedVideos] = useState<Set<string>>(new Set());
  
  // Navigation state
  const [isVideosExpanded, setIsVideosExpanded] = useState(true);
  
  // Activity state
  const [showActivity, setShowActivity] = useState(false);
  const [existingArgument, setExistingArgument] = useState<any>(null);
  
  // Save status for progress persistence
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | 'error' | null>(null);

  // Load Montserrat font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // Fetch content and user data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // PREREQUISITE GATE: Check if user has filed their appeal (protest filed)
        // Step 4 (Prepare Your Case) should only be accessible after appeal is filed
        try {
          const progressRes = await apiClient.courses.getModule('module-01', {});
          if (progressRes.data.success) {
            const moduleProgress = progressRes.data.data.progress || 0;
            if (moduleProgress < 100) {
              console.log('⚠️ Appeal not filed - redirecting to dashboard');
              navigate('/dashboard', { replace: true });
              return;
            }
          }
        } catch (gateErr) {
          console.error('Failed to check appeal status:', gateErr);
          // On error, allow access (don't block the user) - dashboard will gate if needed
        }

        // Fetch module content for Step 4
        const moduleRes = await apiClient.courses.getModule('module-04', {});
        const videos: VideoContent[] = [];

        if (moduleRes.data.success && moduleRes.data.data.content) {
          moduleRes.data.data.content.forEach((content: any) => {
            const metadata = content.metadata || {};
            
            const videoItem: VideoContent = {
              id: content.id,
              title: metadata.title || 'Video',
              description: metadata.description || '',
              duration: formatDuration(metadata.duration || 0),
              durationSeconds: metadata.duration || 0,
              videoUrl: content.contentUrl || '',
              sectionGroup: metadata.sectionGroup || 'tutorial'
            };
            
            videos.push(videoItem);
          });
        }

        setVideoContent(videos);

        // Auto-select first video
        if (videos.length > 0) {
          setActiveVideo(videos[0]);
        }

        // Fetch user's property
        const profileRes = await apiClient.users.getProfile();
        const profile = profileRes.data.data.profile || profileRes.data.data;
        if (profile.properties?.length > 0) {
          setProperty(profile.properties[0]);

          // Check for existing argument
          try {
            const argRes = await apiClient.argument.getByProperty(profile.properties[0].id);
            if (argRes.data.success && argRes.data.data) {
              setExistingArgument(argRes.data.data);
            }
          } catch {
            // No existing argument - that's fine
          }
        } else {
          setError('No property found. Please add a property first.');
        }
      } catch (err: any) {
        console.error('Error fetching Step 4 content:', err);
        setError('Failed to load course content. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const formatDuration = (seconds: number): string => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVideoComplete = () => {
    if (activeVideo) {
      const newCompleted = new Set(completedVideos);
      newCompleted.add(activeVideo.id);
      setCompletedVideos(newCompleted);

      // Auto-advance to next video
      const currentIndex = videoContent.findIndex(v => v.id === activeVideo.id);
      if (currentIndex < videoContent.length - 1) {
        setTimeout(() => {
          setActiveVideo(videoContent[currentIndex + 1]);
        }, 1500);
      }
    }
  };

  const handleShowActivity = () => {
    setShowActivity(true);
    setActiveVideo(null);
  };

  const handleBackToVideos = () => {
    setShowActivity(false);
    if (videoContent.length > 0) {
      setActiveVideo(videoContent[0]);
    }
  };

  // Handle wizard completion - mark module as complete
  const handleWizardComplete = async () => {
    console.log('✅ Build Your Case wizard completed - marking Step 4 module as complete');
    setSaveStatus('saving');
    
    try {
      await apiClient.courses.completeModule(STEP4_MODULE_ID);
      console.log('✅ Step 4 progress saved: 100%');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (progressErr) {
      console.error('Failed to save Step 4 progress:', progressErr);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const calculateProgress = (): number => {
    const total = videoContent.length;
    if (total === 0) return 0;
    return Math.round((completedVideos.size / total) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading Step 4 content...</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Unable to Load Page</h2>
          <p className="text-slate-600 mb-4">{error || 'No property found.'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-slate-800 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Dashboard</span>
            </button>

            {/* Progress */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-600">
                <span className="font-bold text-teal-600">{completedVideos.size}/{videoContent.length}</span> videos watched
              </div>
              {/* Save Status Indicator */}
              {saveStatus && (
                <div className={`text-xs font-medium px-2 py-1 rounded transition-all ${
                  saveStatus === 'saving' ? 'text-slate-500 bg-slate-100' :
                  saveStatus === 'saved' ? 'text-emerald-600 bg-emerald-50' :
                  'text-red-600 bg-red-50'
                }`}>
                  {saveStatus === 'saving' ? 'Saving...' :
                   saveStatus === 'saved' ? '✓ Saved' :
                   '✗ Save failed'}
                </div>
              )}
              <div className="w-32 h-2 bg-slate-200 rounded-full">
                <div 
                  className="h-full bg-teal-500 rounded-full transition-all duration-500"
                  style={{ width: `${calculateProgress()}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Main Content - Left Navigation, Right Video/Activity */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* LEFT SIDE - Navigation */}
          <div className="lg:col-span-4 order-2 lg:order-1">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden sticky top-24">
              {/* Step Header - Primary Identity */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="pl-6">
                  <p className="text-[10px] font-bold text-teal-600 uppercase tracking-widest mb-0.5">Step 4</p>
                  <h2 className="text-base font-bold text-slate-800 leading-tight">Talk with the Assessor</h2>
                </div>
              </div>

              {/* Video Navigation Section - Collapsible */}
              <div className="border-b border-slate-100">
                <button
                  onClick={() => setIsVideosExpanded(!isVideosExpanded)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-teal-600" />
                    <h3 className="text-sm font-semibold text-slate-700">Learn About the Meeting</h3>
                    <span className="text-xs text-slate-400 ml-1">
                      ({completedVideos.size}/{videoContent.length})
                    </span>
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                      isVideosExpanded ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {/* Collapsible Content */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isVideosExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-4 pb-4 space-y-2 max-h-[500px] overflow-y-auto">
                    {videoContent.map((video, index) => {
                      const isActive = video.id === activeVideo?.id && !showActivity;
                      const isCompleted = completedVideos.has(video.id);

                      return (
                        <button
                          key={video.id}
                          onClick={() => {
                            setActiveVideo(video);
                            setShowActivity(false);
                          }}
                          className={`w-full text-left p-3 rounded-xl transition-all ${
                            isActive
                              ? 'bg-teal-50 border-2 border-teal-300'
                              : isCompleted
                              ? 'bg-emerald-50 border-2 border-emerald-200'
                              : 'bg-slate-50 border-2 border-slate-100 hover:border-slate-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              isCompleted ? 'bg-emerald-200' : isActive ? 'bg-teal-200' : 'bg-slate-200'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                              ) : isActive ? (
                                <Play className="w-4 h-4 text-teal-700" />
                              ) : (
                                <span className="text-slate-600 font-bold text-sm">{index + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className={`font-medium text-sm leading-tight ${
                                isActive ? 'text-teal-900' : isCompleted ? 'text-emerald-900' : 'text-slate-900'
                              }`}>
                                {video.title}
                              </h4>
                              <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                                <Clock className="w-3 h-3" />
                                <span>{video.duration}</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Build Your Presentation Activity Section */}
              <div className="px-4 py-3 bg-gradient-to-b from-teal-50/50 to-white">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-teal-600" />
                  <h3 className="text-sm font-semibold text-slate-700">Prepare Your Presentation</h3>
                </div>
                
                <p className="text-sm text-slate-600 mb-4">
                  Review your evidence and generate your presentation script for the meeting.
                </p>
                
                <button
                  onClick={handleShowActivity}
                  className={`w-full py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                    showActivity
                      ? 'bg-slate-900 text-white'
                      : 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                  }`}
                >
                  <Sparkles className="w-5 h-5" />
                  {existingArgument ? 'View Your Meeting Kit' : 'Build Your Case'}
                  <ChevronRight className="w-4 h-4" />
                </button>

                {existingArgument && (
                  <p className="text-xs text-slate-500 text-center mt-2">
                    Script generated: {new Date(existingArgument.generatedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Video Player OR Activity */}
          <div className="lg:col-span-8 order-1 lg:order-2 space-y-6">
            {showActivity ? (
              /* Show Build Your Case Wizard */
              <BuildYourCaseWizard
                propertyId={property.id}
                existingArgument={existingArgument}
                onComplete={handleWizardComplete}
                onBack={handleBackToVideos}
              />
            ) : (
              /* Show Video Player */
              <>
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                  {activeVideo ? (
                    <>
                      <VideoPlayer
                        key={activeVideo.id}
                        videoUrl={activeVideo.videoUrl}
                        onVideoComplete={handleVideoComplete}
                        currentProgress={0}
                        onProgress={() => {}}
                      />
                      <div className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-slate-900 mb-2">
                              {activeVideo.title}
                            </h3>
                            <p className="text-slate-600">{activeVideo.description}</p>
                          </div>
                          {completedVideos.has(activeVideo.id) && (
                            <div className="flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                              <CheckCircle2 className="w-4 h-4" />
                              Watched
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="aspect-video bg-slate-100 flex items-center justify-center">
                      <p className="text-slate-500">Select a video to start learning</p>
                    </div>
                  )}
                </div>

                {/* What You'll Learn Card */}
                {activeVideo?.sectionGroup === 'intro' && (
                  <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl border border-teal-200 p-6">
                    <h2 className="text-lg font-bold text-slate-900 mb-4">What You'll Learn in Step 4</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <p className="text-slate-700 text-sm">What to expect during the informal meeting</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <p className="text-slate-700 text-sm">How to structure your argument effectively</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <p className="text-slate-700 text-sm">Walk through your evidence step by step</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <p className="text-slate-700 text-sm">Handle counter offers and next steps</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Bottom Navigation - Only show when viewing videos, not in wizard */}
            {!showActivity && (
              <div className="flex justify-between">
                <button
                  onClick={() => navigate('/step/3/find-flaws')}
                  className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Step 3
                </button>

                {/* Right CTA: Next Video when available, otherwise Return to Dashboard */}
                {activeVideo && (() => {
                  const currentIndex = videoContent.findIndex(v => v.id === activeVideo.id);
                  const hasNextVideo = currentIndex < videoContent.length - 1;
                  
                  if (hasNextVideo) {
                    return (
                      <button
                        onClick={() => {
                          setActiveVideo(videoContent[currentIndex + 1]);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                      >
                        Next Video
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    );
                  } else {
                    return (
                      <button
                        onClick={() => navigate('/dashboard')}
                        className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                      >
                        Return to Dashboard
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    );
                  }
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArgumentsPage;
